import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { generateUid } from '../../utils/uid';
import '../../elements/asc-card/index';

const ARROW_ICON = html`<svg
  width="24"
  height="24"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M7 17L17 7M7 7h10v10"
    stroke="currentColor"
    stroke-width="1.67"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/**
 * A content-teaser card for article and blog feed layouts.
 *
 * Presents a title, optional flush image, optional author/byline, description,
 * and topic badge pills inside a bordered card shell. When `href` is set, the
 * entire card surface is clickable via a stretched native `<a>` (rendered
 * internally) and a diagonal arrow icon appears in the heading row. All visible
 * content — headings, paragraphs, and images — flows through slots so it is
 * immediately indexed by search engine crawlers. The component owns layout,
 * visual chrome, and hover/focus behaviour only.
 *
 * Composes `asc-card` (card shell), `asc-badge` (tags slot), and `asc-avatar`
 * (author slot) via consumer-owned slots.
 *
 * @element asc-feed-card
 *
 * @slot - Card title. Consumer is responsible for wrapping in the appropriate
 *   heading element (e.g. `<h3>`) for correct document outline. Required.
 * @slot image - Thumbnail image. Accepts `<img>` or `<picture>`. When populated
 *   the wrapper bleeds flush to the top, left, and right inner edges at 3:2
 *   aspect ratio. Optional — wrapper is not rendered when slot is empty.
 * @slot byline - Short author + date string (e.g. `"Olivia Rhye · 20 Jan 2025"`).
 *   Rendered above the heading row in text-sm/semibold, brand secondary colour.
 * @slot description - Body paragraph. Rendered below the heading row.
 * @slot author - Rich author identity. Accepts `<asc-avatar>`. Rendered above
 *   the tags area. Optional — author area is not rendered when slot is empty.
 * @slot tags - One or more `<asc-badge>` elements for topic categorisation.
 *   Rendered at the bottom of the body. Optional — tags area is not rendered
 *   when slot is empty.
 *
 * @csspart card-base - Full card chrome (border, shadow, radius, bg) exported
 *   from `asc-card`. Target for layout overrides such as `max-width`.
 * @csspart link - Stretched `<a>` covering the full card surface. Only rendered
 *   when `href` is set. Target for custom focus ring overrides.
 * @csspart image-area - Flush image container. Only rendered when the `image`
 *   slot is populated.
 * @csspart body - Flex-column content container. `flex: 1` to fill available
 *   height.
 * @csspart heading-row - Flex row containing the default (title) slot and the
 *   optional arrow icon.
 * @csspart heading - Wrapper around the default slot. Used for `aria-labelledby`
 *   on the stretched link and for colour transitions on hover/focus.
 * @csspart arrow - Wrapper for the ↗ SVG icon. Only rendered when `href` is set.
 *   Nudges up-and-right and shifts to the brand colour on card link hover/focus
 *   (nudge is suppressed under `prefers-reduced-motion`).
 * @csspart description-area - Wrapper for the `description` slot.
 * @csspart author-area - Wrapper for the `author` slot. Only rendered when the
 *   slot is populated.
 * @csspart tags-area - Wrapper for the `tags` slot. Only rendered when the slot
 *   is populated.
 *
 * @cssprop --asc-feed-card-image-height - Fixed height of the image area in `vertical` layout.
 *   Default: `240px`
 * @cssprop --asc-feed-card-image-width - Width of the leading image panel in `horizontal` layout.
 *   Default: `320px`
 * @cssprop --asc-feed-card-min-height - Minimum height of the card in `horizontal` layout.
 *   Default: `220px`
 * @cssprop --asc-feed-card-body-gap - Gap between sections inside the body.
 *   Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-feed-card-padding - Padding applied to the body area.
 *   Default: `var(--asc-spacing-3xl)`
 * @cssprop --asc-feed-card-heading-color - Default heading text colour.
 *   Default: `var(--asc-text-primary)`
 * @cssprop --asc-feed-card-heading-hover-color - Heading and arrow colour on card link hover/focus.
 *   Default: `var(--asc-text-brand-primary)`
 * @cssprop --asc-feed-card-border-hover-color - Card border colour on card link hover/focus.
 *   Only applies when `href` is set. Default: `var(--asc-border-brand)`
 * @cssprop --asc-feed-card-tags-gap - Gap between tag badges in the tags area.
 *   Default: `var(--asc-spacing-sm)`
 *
 * @prop {boolean} borderless - When set, removes the card border, border-radius, and box-shadow.
 * @prop {'vertical'|'horizontal'} layout - Controls the arrangement of the image and body panels.
 * @prop {string} target - Forwarded to the inner `<a>` as its `target` attribute. Only used when `href` is set.
 */
@customElement('asc-feed-card')
export class AscFeedCard extends LitElement {

  /** When set, removes the card border, border-radius, and box-shadow (matches the Figma flat/borderless layout). */
  @property({ type: Boolean, reflect: true }) borderless = false;

  /**
   * Controls the arrangement of the image and body panels.
   * - `vertical` — image sits above the body content (default)
   * - `horizontal` — image occupies a fixed-width leading panel; body fills the rest.
   *   Collapses back to vertical on viewports ≤ 575 px.
   */
  @property({ type: String, reflect: true }) layout: 'vertical' | 'horizontal' = 'vertical';

  static override styles = css`
    :host {
      display: block;
      min-width: 0;
      max-width: 100%;
    }

    asc-card {
      height: 100%; /* fill host when grid uses items-stretch */
    }

    /* ── asc-card shell overrides ──────────────────────────────────────────────────────── */
    asc-card::part(base) {
      padding: 0; /* asc-feed-card owns all internal spacing */
      display: flex;
      flex-direction: column;
      position: relative; /* required for stretched link */
      border-radius: var(--asc-radius-xl); /* Figma: --spacing-xl; asc-card defaults to --asc-radius-lg */
      height: 100%; /* fill asc-card so .body flex: 1 works in stretch grids */
      transition: border-color var(--asc-transition-fast, 100ms ease);
    }

    /* ── Border colour on link hover/focus (only when card is a link) ──────── */
    /* The stretched .card-link covers the full surface, so hovering anywhere on  */
    /* the card hovers the link; :focus-within catches keyboard focus on it.      */
    /* The border lives in asc-card's shadow [part=base], unreachable by a sibling */
    /* combinator from the link, so we scope on :host([href]) instead.            */
    :host([href]) asc-card:hover::part(base),
    :host([href]) asc-card:focus-within::part(base) {
      border-color: var(--asc-feed-card-border-hover-color, var(--asc-border-brand));
    }
    /* asc-card overflow: hidden is kept — clips flush image at rounded corners */

    /* ── Horizontal layout ──────────────────────────────────────────────────── */
    /* All asc-feed-card content is slotted into asc-card's [part="body"] via    */
    /* its default slot — so flex-direction: row must be applied there, not on   */
    /* [part="base"] which only wraps asc-card's own image/body panels.          */
    :host([layout='horizontal']) asc-card::part(base) {
      min-height: var(--asc-feed-card-min-height, 220px);
    }

    :host([layout='horizontal']) asc-card::part(body) {
      display: flex;
      flex-direction: row;
    }

    /* ── Borderless variant ─────────────────────────────────────────────────── */
    :host([borderless]) asc-card::part(base) {
      border: none;
      border-radius: 0;
      box-shadow: none;
    }

    :host([borderless]) .card-link {
      border-radius: 0;
    }

    :host([borderless]) .body {
      padding-inline: 0;
    }

    /* ── Stretched link ────────────────────────────────────────────────────── */
    .card-link {
      position: absolute;
      inset: 0;
      z-index: 1;
      border-radius: var(--asc-radius-xl);
      text-decoration: none;
    }

    .card-link:focus-visible {
      outline: var(--asc-focus-ring-width, 2px) solid var(--asc-focus-ring);
      outline-offset: var(--asc-focus-ring-offset, 2px);
    }

    /* ── Heading colour on link hover/focus (sibling combinator) ───────────── */
    .card-link:hover ~ .body .heading,
    .card-link:focus-visible ~ .body .heading {
      color: var(--asc-feed-card-heading-hover-color, var(--asc-text-brand-primary));
    }

    .heading {
      transition: color var(--asc-transition-fast, 100ms ease);
    }

    /* ── Arrow nudge + colour on link hover/focus ──────────────────────────── */
    /* The arrow only renders when href is set, so these rules are inherently    */
    /* scoped to linked cards — no extra :host([href]) guard needed.             */
    .card-link:hover ~ .body .arrow,
    .card-link:focus-visible ~ .body .arrow {
      color: var(--asc-feed-card-heading-hover-color, var(--asc-text-brand-primary));
      transform: translate(2px, -2px);
    }

    .arrow {
      transition:
        color var(--asc-transition-fast, 100ms ease),
        transform var(--asc-transition-fast, 100ms ease);
    }

    /* Respect users who prefer reduced motion — keep the colour cue, drop the nudge. */
    @media (prefers-reduced-motion: reduce) {
      .arrow {
        transition: color var(--asc-transition-fast, 100ms ease);
      }

      .card-link:hover ~ .body .arrow,
      .card-link:focus-visible ~ .body .arrow {
        transform: none;
      }
    }

    /* ── Image area ─────────────────────────────────────────────────────────── */
    .image-area {
      flex-shrink: 0;
      overflow: hidden;
    }

    :host(:not([layout='horizontal'])) .image-area {
      height: var(--asc-feed-card-image-height, 240px); /* no-token: component-specific fixed height */
    }

    :host([layout='horizontal']) .image-area {
      width: var(--asc-feed-card-image-width, 320px);
      height: auto;
      align-self: stretch;
    }

    /* ── Mobile: collapse horizontal to vertical at ≤ 575px ────────────────── */
    @media (max-width: 575px) {
      :host([layout='horizontal']) asc-card::part(base) {
        min-height: unset;
      }

      :host([layout='horizontal']) asc-card::part(body) {
        flex-direction: column;
      }

      :host([layout='horizontal']) .image-area {
        width: 100%;
        height: var(--asc-feed-card-image-height, 240px);
      }
    }


    /* ── Body ───────────────────────────────────────────────────────────────── */
    .body {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--asc-feed-card-body-gap, var(--asc-spacing-xl));
      padding: var(--asc-feed-card-padding, var(--asc-spacing-3xl));
    }

    /* ── Byline ─────────────────────────────────────────────────────────────── */
    .byline-wrapper {
      color: var(--asc-text-brand-secondary);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-semibold);
      line-height: var(--asc-line-height-text-sm);
    }

    .byline-wrapper ::slotted(*) {
      margin: 0;
      padding: 0;
      font: inherit;
      color: inherit;
    }

    /* ── Heading row ────────────────────────────────────────────────────────── */
    .heading-row {
      display: flex;
      align-items: flex-start;
      gap: var(--asc-spacing-xl);
    }

    .heading {
      flex: 1;
      color: var(--asc-feed-card-heading-color, var(--asc-text-primary));
      font-size: var(--asc-font-size-text-lg);
      font-weight: var(--asc-font-weight-semibold);
      line-height: var(--asc-line-height-text-lg);
    }

    .heading ::slotted(*) {
      margin: 0;
      padding: 0;
      font: inherit;
      color: inherit;
    }

    .arrow {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      color: var(--asc-text-primary);
    }

    /* ── Description ────────────────────────────────────────────────────────── */
    .description-area {
      color: var(--asc-text-tertiary);
      font-size: var(--asc-font-size-text-md);
      font-weight: var(--asc-font-weight-regular);
      line-height: var(--asc-line-height-text-md);
    }

    .description-area ::slotted(*) {
      margin: 0;
      padding: 0;
      font: inherit;
      color: inherit;
    }

    /* ── Card footer (author + tags) ─────────────────────────────────────────── */
    /* Single margin-top: auto pins the whole footer group to the bottom of .body  */
    /* whether one or both slots are populated.                                     */
    .card-footer {
      margin-top: auto;
      display: flex;
      flex-direction: column;
      gap: var(--asc-feed-card-body-gap, var(--asc-spacing-xl));
    }

    /* ── Tags area ───────────────────────────────────────────────────────────── */
    /* The slot is the only flex child of tags-area, so flex layout must be      */
    /* applied to the slot itself so that each distributed badge becomes a        */
    /* flex item and row-gap applies when tags wrap.                              */
    [part='tags-area'] slot {
      display: flex;
      flex-wrap: wrap;
      column-gap: var(--asc-feed-card-tags-gap, var(--asc-spacing-sm));
      row-gap: var(--asc-feed-card-tags-gap, var(--asc-spacing-sm));
    }

    /* ── Empty-slot sentinel ─────────────────────────────────────────────────── */
    /* Hidden sentinel slots are direct flex children of .body. The [hidden]      */
    /* attribute sets display:none via UA styles but shadow DOM doesn't always     */
    /* honour it reliably, so we force it explicitly to prevent phantom flex gaps. */
    .body slot[hidden] {
      display: none;
    }
  `;

  /** When set, renders a stretched `<a>` covering the full card surface and shows the arrow icon. */
  @property({ type: String, reflect: true }) href: string | undefined = undefined;

  /** Forwarded to the inner `<a>` as its `target` attribute (e.g. `"_blank"`). Only used when `href` is set. */
  @property({ type: String }) target: string | undefined = undefined;

  @state() private _hasImage = false;
  @state() private _hasByline = false;
  @state() private _hasDescription = false;
  @state() private _hasAuthor = false;
  @state() private _hasTags = false;

  private readonly _uid = generateUid();

  /** Tracks which elements currently have image styles applied. */
  private _styledImageElements = new Set<HTMLElement>();

  /** Snapshots of pre-existing inline styles, keyed by element, restored on cleanup. */
  private _savedImageStyles = new WeakMap<HTMLElement, Map<string, string>>();

  private static readonly _IMAGE_STYLE_PROPS = ['display', 'width', 'height', 'object-fit'] as const;

  private _applyImageStyles(el: HTMLElement): void {
    if (!this._savedImageStyles.has(el)) {
      const snapshot = new Map<string, string>();
      for (const prop of AscFeedCard._IMAGE_STYLE_PROPS) {
        snapshot.set(prop, el.style.getPropertyValue(prop));
      }
      this._savedImageStyles.set(el, snapshot);
    }
    // Inline styles override base.css's `img { max-width: 100% }` light DOM author rule,
    // which outranks ::slotted() shadow styles in the cascade (same technique as asc-card).
    el.style.setProperty('display', 'block');
    el.style.setProperty('width', '100%');
    el.style.setProperty('height', '100%');
    el.style.setProperty('object-fit', 'cover');

    // For <picture>, also style the inner <img> so it fills/crops correctly.
    if (el instanceof HTMLPictureElement) {
      const img = el.querySelector('img');
      if (img) {
        if (!this._savedImageStyles.has(img)) {
          const imgSnapshot = new Map<string, string>();
          for (const prop of AscFeedCard._IMAGE_STYLE_PROPS) {
            imgSnapshot.set(prop, img.style.getPropertyValue(prop));
          }
          this._savedImageStyles.set(img, imgSnapshot);
        }
        img.style.setProperty('display', 'block');
        img.style.setProperty('width', '100%');
        img.style.setProperty('height', '100%');
        img.style.setProperty('object-fit', 'cover');
        this._styledImageElements.add(img);
      }
    }
  }

  private _restoreImageStyles(el: HTMLElement): void {
    const saved = this._savedImageStyles.get(el);
    if (!saved) return;
    for (const prop of AscFeedCard._IMAGE_STYLE_PROPS) {
      const original = saved.get(prop) ?? '';
      if (original) {
        el.style.setProperty(prop, original);
      } else {
        el.style.removeProperty(prop);
      }
    }
    this._savedImageStyles.delete(el);

    // Also restore inner <img> styles for <picture> elements.
    if (el instanceof HTMLPictureElement) {
      const img = el.querySelector('img');
      if (img && this._styledImageElements.has(img)) {
        this._restoreImageStyles(img);
        this._styledImageElements.delete(img);
      }
    }
  }

  private _onImageSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    const elements = slot.assignedElements();
    this._hasImage = elements.length > 0;

    const nextStyled = new Set<HTMLElement>();
    for (const el of elements) {
      if (el instanceof HTMLElement) {
        this._applyImageStyles(el);
        nextStyled.add(el);
      }
    }

    // Restore styles on elements that have left the slot.
    for (const el of this._styledImageElements) {
      if (!nextStyled.has(el)) {
        this._restoreImageStyles(el);
      }
    }
    this._styledImageElements = nextStyled;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    for (const el of this._styledImageElements) {
      this._restoreImageStyles(el);
    }
    this._styledImageElements.clear();
  }

  private _onBylineSlotChange(e: Event): void {
    this._hasByline = (e.target as HTMLSlotElement).assignedNodes({ flatten: true }).length > 0;
  }

  private _onDescriptionSlotChange(e: Event): void {
    this._hasDescription = (e.target as HTMLSlotElement).assignedNodes({ flatten: true }).length > 0;
  }

  private _onAuthorSlotChange(e: Event): void {
    this._hasAuthor = (e.target as HTMLSlotElement).assignedElements().length > 0;
  }

  private _onTagsSlotChange(e: Event): void {
    this._hasTags = (e.target as HTMLSlotElement).assignedElements().length > 0;
  }

  override render() {
    return html`
      <asc-card exportparts="base: card-base">
        ${this.href
          ? html`
              <a
                part="link"
                class="card-link"
                href="${this.href}"
                target=${this.target ?? nothing}
                aria-labelledby="heading-${this._uid}"
              ></a>
            `
          : nothing}

        ${this._hasImage
          ? html`
              <div part="image-area" class="image-area">
                <slot name="image" @slotchange=${this._onImageSlotChange}></slot>
              </div>
            `
          : html`<slot
              name="image"
              @slotchange=${this._onImageSlotChange}
              hidden
            ></slot>`}

        <div part="body" class="body">
          ${this._hasByline
            ? html`
                <div class="byline-wrapper">
                  <slot name="byline" @slotchange=${this._onBylineSlotChange}></slot>
                </div>
              `
            : html`<slot name="byline" @slotchange=${this._onBylineSlotChange} hidden></slot>`}

          <div part="heading-row" class="heading-row">
            <span part="heading" class="heading" id="heading-${this._uid}">
              <slot></slot>
            </span>
            ${this.href
              ? html`<span part="arrow" class="arrow" aria-hidden="true"
                  >${ARROW_ICON}</span
                >`
              : nothing}
          </div>

          ${this._hasDescription
            ? html`
                <div part="description-area" class="description-area">
                  <slot name="description" @slotchange=${this._onDescriptionSlotChange}></slot>
                </div>
              `
            : html`<slot name="description" @slotchange=${this._onDescriptionSlotChange} hidden></slot>`}

          ${this._hasAuthor || this._hasTags
            ? html`
                <div class="card-footer">
                  ${this._hasAuthor
                    ? html`
                        <div part="author-area">
                          <slot
                            name="author"
                            @slotchange=${this._onAuthorSlotChange}
                          ></slot>
                        </div>
                      `
                    : html`<slot
                        name="author"
                        @slotchange=${this._onAuthorSlotChange}
                        hidden
                      ></slot>`}
                  ${this._hasTags
                    ? html`
                        <div part="tags-area">
                          <slot
                            name="tags"
                            @slotchange=${this._onTagsSlotChange}
                          ></slot>
                        </div>
                      `
                    : html`<slot
                        name="tags"
                        @slotchange=${this._onTagsSlotChange}
                        hidden
                      ></slot>`}
                </div>
              `
            : html`
                <slot
                  name="author"
                  @slotchange=${this._onAuthorSlotChange}
                  hidden
                ></slot>
                <slot
                  name="tags"
                  @slotchange=${this._onTagsSlotChange}
                  hidden
                ></slot>
              `}
        </div>
      </asc-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-feed-card': AscFeedCard;
  }
}
